import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  PasswordInput,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { resetPasswordRequest } from './auth-api';
import { ApiError } from '../lib/api-error';

const MIN_LENGTH = 12;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  const mismatched = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      await resetPasswordRequest(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_or_expired_token') {
        setError(
          'This reset link is invalid or has expired. Request a new one from the forgot-password page.',
        );
      } else if (err instanceof ApiError && err.code === 'validation_failed') {
        setError(`Password must be at least ${MIN_LENGTH} characters.`);
      } else {
        setError('Could not reach the server. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh" bg="var(--mantine-color-default-hover)">
      <Paper shadow="md" radius="md" p="xl" w={420} withBorder>
        <Stack gap="md">
          <Box>
            <Title order={2}>Set a new password</Title>
            <Text c="dimmed" size="sm">
              Choose a password at least {MIN_LENGTH} characters long.
            </Text>
          </Box>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {success ? (
            <Alert icon={<IconCheck size={16} />} color="pine" variant="light">
              <Text size="sm">
                Password updated. Redirecting you to sign in…
              </Text>
            </Alert>
          ) : (
            <form onSubmit={onSubmit}>
              <Stack gap="sm">
                <PasswordInput
                  label="New password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                  autoFocus
                  error={tooShort ? `At least ${MIN_LENGTH} characters` : undefined}
                />
                <PasswordInput
                  label="Confirm new password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.currentTarget.value)}
                  required
                  error={mismatched ? 'Does not match' : undefined}
                />
                <Button
                  type="submit"
                  loading={submitting}
                  disabled={mismatched || tooShort || password.length === 0}
                  fullWidth
                  mt="xs"
                >
                  Update password
                </Button>
              </Stack>
            </form>
          )}

          <Text size="xs" c="dimmed" ta="center">
            <Anchor component={Link} to="/login">
              Back to sign in
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
}
